import { Inject, Injectable } from "@angular/core";
import { FormBuilder, FormGroup, Validators, ValidationErrors } from "@angular/forms";
import {
    NotificationPreferences,
    NotificationPreferencesByGroup,
    NotificationPreferencesDescription
} from "@nms-ng2/app/shared/services/notification-preferences/notification-preferences-model";
import { UserConfigPreferencesPanel, UserPreferencesPanelTab } from "./user-preferences.models";
import {
    ANGULARJS_TRANSLATE,
    SYSTEM_USER_CONFIGURATION_SERVICE
} from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";

/**
 * Serviço para auxiliar na construção e validação dos formulários da tela
 * de configurações do usuário.
 */
@Injectable({
    providedIn: "root"
})
export class UserConfigPreferencesService {
    readonly minPurgeOlderNumber = 1;
    readonly maxPurgeOlderNumber = 30;
    readonly minDelayAutoCloseNotification = 1;
    readonly maxDelayAutoCloseNotification = 1800;
    private readonly maxLengthEmail = 60;
    private readonly emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    private maxCharsPassword = 40;
    private minCharsPassword = 6;
    private readonly maxCharsName = 50;
    private readonly minCharsName = 2;

    /**
     * Pattern que permite apenas valores entre 1 e 1800,
     * utilizado para validar o campo de configuração de segundos
     * para fechamento automático da notificação.
     */
    readonly notificationCloseDelayPattern: string = "^((1800)|(1[0-7][0-9]{2})|([1-9][0-9]{2})|([1-9][0-9]{1})|([1-9]))$";
    private notificationsDescriptionMap: NotificationPreferencesDescription = {
        DISCOVERED_ADDED_DEVICE: {
            SUCCESS: this.translate.instant("manage.user.notification.preferences.type.discovery.add.device.tooltip")
        },
        SCHEDULED_BY_FREQUENCY: {
            INFO: this.translate.instant("manage.user.notification.preferences.type.scheduled.execution.tooltip")
        },
        SCHEDULED_BY_TR069_EVENT: {
            INFO: this.translate.instant("manage.user.notification.preferences.type.scheduled.execution.tooltip")
        },
        SCHEDULED_BY_SNMP_TRAP: {
            INFO: this.translate.instant("manage.user.notification.preferences.type.scheduled.execution.tooltip")
        },
        SCHEDULED_BY_FREQUENCY_ADMIN: {
            INFO: this.translate.instant("manage.user.notification.preferences.type.only.admin.users.tooltip")
        },
        SCHEDULED_BY_TR069_EVENT_ADMIN: {
            INFO: this.translate.instant("manage.user.notification.preferences.type.only.admin.users.tooltip")
        },
        SCHEDULED_BY_SNMP_TRAP_ADMIN: {
            INFO: this.translate.instant("manage.user.notification.preferences.type.only.admin.users.tooltip")
        },
        CPE_DOWNLOAD_COMPLETED: {
            SUCCESS: this.translate.instant("manage.user.notification.preferences.type.cpe.download.completed.tooltip")
        },
        CPE_DOWNLOAD_PENDING: {
            INFO: this.translate.instant("manage.user.notification.preferences.type.cpe.download.pending.tooltip")
        },
        CPE_DOWNLOAD_FAILED: {
            ERROR: this.translate.instant("manage.user.notification.preferences.type.cpe.download.failed.tooltip")
        },
        CPE_TRANSFER_COMPLETED: {
            SUCCESS: this.translate.instant("manage.user.notification.preferences.type.cpe.transfer.completed.tooltip")
        },
        CPE_TRANSFER_FAILED: {
            ERROR: this.translate.instant("manage.user.notification.preferences.type.cpe.transfer.failed.tooltip")
        }
    };

    constructor(private readonly formBuilder: FormBuilder,
                @Inject(ANGULARJS_TRANSLATE) private readonly translate: any,
                @Inject(SYSTEM_USER_CONFIGURATION_SERVICE) private readonly systemUserConfigService: any) {
    }

    getNotificationFormGroups(notifications: NotificationPreferences[]): FormGroup {
        const notificationsControls = [];

        for (let i = 0; i < notifications.length; i++) {
            const { delayBeforeCloseInSeconds, autoClose, enabled, notificationStatus, notificationType, description } =
                notifications[i];
            notificationsControls.push({
                delayBeforeCloseInSeconds: this.formBuilder.control(delayBeforeCloseInSeconds, [
                    Validators.required,
                    Validators.pattern(this.notificationCloseDelayPattern),
                    Validators.max(this.maxDelayAutoCloseNotification),
                    Validators.min(this.minDelayAutoCloseNotification)
                ]),
                autoClose,
                enabled,
                notificationStatus,
                notificationType,
                description
            });
        }

        const notificationFormArrayGroup = this.formBuilder.array(
            notificationsControls.map((notificationControl) => this.formBuilder.group(notificationControl))
        );

        return this.formBuilder.group({ notifications: notificationFormArrayGroup });
    }

    getGeneralDataFormGroup(doNotDisturb: boolean, purgeOlderInDays: number): FormGroup {
        return this.formBuilder.group({
            doNotDisturb: doNotDisturb,
            purgeOlderInDays: this.formBuilder.control(purgeOlderInDays, [
                Validators.required,
                Validators.min(this.minPurgeOlderNumber),
                Validators.max(this.maxPurgeOlderNumber)
            ])
        });
    }

    getUserDataFormGroup(userData): FormGroup {
        return this.formBuilder.group({
            user: [userData.user],
            name: [
                userData.name,
                [Validators.minLength(this.minCharsName), Validators.maxLength(this.maxCharsName)]
            ],
            email: [
                userData.email,
                [Validators.pattern(this.emailPattern), Validators.required, Validators.maxLength(this.maxLengthEmail)]
            ],
            phone: [userData.phone]
        });
    }

    setSystemConfigCharsLength() {
        this.systemUserConfigService.getSecuritySystemConfig().then((sysConfig) => {
            this.maxCharsPassword = sysConfig.maxCharsPassword;
            this.minCharsPassword = sysConfig.minCharsPassword;
        });
    }

    getInvalidTabs(userConfigPreferencesFormGroup: FormGroup, panelsList: UserConfigPreferencesPanel[]) {
        const tabs: UserPreferencesPanelTab[] = panelsList.flatMap((panel: UserConfigPreferencesPanel) => panel.tabs);

        return tabs.filter((tab: UserPreferencesPanelTab) => {
            return userConfigPreferencesFormGroup.controls[tab.id] && userConfigPreferencesFormGroup.controls[tab.id].invalid;
        });
    }

    getChangePasswordFormGroup() {
        const passwordValidators = [
            Validators.minLength(this.minCharsPassword),
            Validators.maxLength(this.maxCharsPassword)
        ];

        return this.formBuilder.group(
            {
                currentPassword: [""],
                password: ["", passwordValidators],
                confirmPassword: ["", passwordValidators]
            },
            { validators: this.passwordFormValidator() }
        );
    }

    hasSomeNotificationConfigChange(
        notificationsByGroupCache: NotificationPreferencesByGroup,
        notificationsByGroup: NotificationPreferencesByGroup,
        userConfigPreferencesFormGroup: FormGroup
    ): boolean {
        const notificationsGroups = Object.keys(notificationsByGroup);
        const notificationTypeWithChanges = notificationsGroups.filter((notificationType) => {
            if (userConfigPreferencesFormGroup.controls[notificationType]) {
                let hasChanges = false;
                const notificationsFormGroup = userConfigPreferencesFormGroup.controls[notificationType] as FormGroup;
                const notificationsFormControl = notificationsFormGroup.getRawValue()["notifications"];
                const changedNotifications = notificationsFormControl.filter((notificationFromForm: NotificationPreferences) => {
                    const currentNotificationCache: NotificationPreferences = this.findCurrentNotification(
                        notificationsByGroupCache,
                        notificationType,
                        notificationFromForm
                    );

                    const currentNotification: NotificationPreferences = this.findCurrentNotification(
                        notificationsByGroup,
                        notificationType,
                        notificationFromForm
                    );

                    if (currentNotification) {
                        const isSameNotificationType =
                            currentNotification.notificationType === notificationFromForm.notificationType;
                        const isSameNotificationStatus =
                            currentNotification.notificationStatus === notificationFromForm.notificationStatus;

                        if (isSameNotificationType && isSameNotificationStatus) {
                            hasChanges =
                                currentNotification.enabled !== notificationFromForm.enabled ||
                                currentNotification.autoClose !== notificationFromForm.autoClose ||
                                currentNotification.delayBeforeCloseInSeconds !== notificationFromForm.delayBeforeCloseInSeconds;

                            if (hasChanges) {
                                this.updateNotificationValues(currentNotificationCache, notificationFromForm);
                            }
                        }

                        return hasChanges;
                    }
                });

                return changedNotifications.length > 0;
            }
        });

        return notificationTypeWithChanges.length > 0;
    }

    public setNotificationsDescription(notificationPreferences: NotificationPreferencesByGroup) {
        Object.keys(notificationPreferences).forEach((key) => {
            notificationPreferences[key].forEach((notificationPreference) => {
                notificationPreference.description = this.getNotificationPreferencesTooltip(notificationPreference);
            });
        });
    }

    private getNotificationPreferencesTooltip(notification: NotificationPreferences): string {
        if (this.notificationsDescriptionMap[notification.notificationType]) {
            return this.notificationsDescriptionMap[notification.notificationType][notification.notificationStatus];
        }

        return notification.admin ? this.translate.instant("manage.user.notification.preferences.type.admin.tooltip") : null;
    }

    private findCurrentNotification(
        notificationsByGroup: NotificationPreferencesByGroup,
        notificationType: string,
        notificationFromForm: NotificationPreferences
    ) {
        return notificationsByGroup[notificationType].find(
            (notificationByGroup: NotificationPreferences) =>
                notificationByGroup.notificationStatus === notificationFromForm.notificationStatus &&
                notificationByGroup.notificationType === notificationFromForm.notificationType
        );
    }

    private updateNotificationValues(
        notificationByGroup: NotificationPreferences,
        changedNotificationData: NotificationPreferences
    ) {
        notificationByGroup.enabled = changedNotificationData.enabled;
        notificationByGroup.autoClose = changedNotificationData.autoClose;
        notificationByGroup.delayBeforeCloseInSeconds = changedNotificationData.delayBeforeCloseInSeconds;
    }

    private passwordFormValidator(): ValidationErrors | null {
        return (formGroup: FormGroup) => {
            const currentPasswordControl = formGroup.get("currentPassword");
            const newPasswordControl = formGroup.get("password");
            const confirmPasswordControl = formGroup.get("confirmPassword");
            const isMatching = !confirmPasswordControl.value ? true : newPasswordControl.value === confirmPasswordControl.value;
            const currentAndNewMatching = !newPasswordControl.value
                ? false
                : newPasswordControl.value === currentPasswordControl.value;
            const allFieldsValues = [currentPasswordControl.value, newPasswordControl.value, confirmPasswordControl.value];
            const isAllFieldsEmpty = allFieldsValues.every((field) => !field?.trim());
            let errors: ValidationErrors = {};

            if (isAllFieldsEmpty) {
                return null;
            }

            if (!isMatching) {
                errors["mustMatch"] = true;
            } else {
                if (!newPasswordControl.value) {
                    errors["newPasswordControlRequired"] = true;
                }

                if (!confirmPasswordControl.value) {
                    errors["confirmPasswordControlRequired"] = true;
                }
            }

            if (!currentPasswordControl.value) {
                errors["currentPasswordEmpty"] = true;
            }

            if (currentAndNewMatching) {
                errors["newPasswordEqualsCurrent"] = true;
            }

            return errors;
        };
    }
}
